import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Icons,
  iconSize,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@munifocus/ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  COST_RECOVERY_LIMITS,
  PAYMENT_METHODS,
  PLAN_FREQUENCIES,
  addDays,
  planSchedule,
  type CostDocumentLocale,
  type CostRecoveryCaseView,
  type EscalationKind,
  type PaymentMethod,
  type PlanFrequency,
} from '@munifocus/contracts-gov';
import {
  useCreatePlan,
  useEscalate,
  useIssueStatement,
  useRecordPayment,
} from '../../lib/cost-recovery-api';
import { day, money, parseMoney, rate, todayIso } from '../../lib/cost-recovery-display';

const ISO = /^\d{4}-\d{2}-\d{2}$/;

/** Asks for the reason a void or a rejection needs, and nothing else. */
export function ReasonDialog({
  open,
  title,
  body,
  confirmLabel,
  busy,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  busy: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{body}</DialogContentText>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          label={t('gov.costRecovery.reason')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          slotProps={{ htmlInput: { maxLength: 1000 } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button
          variant="contained"
          color="error"
          disabled={reason.trim().length < 3 || busy}
          onClick={() => onConfirm(reason.trim())}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Bill every approved cost on a statement. Says what it will bill and when it
 * falls due before anything is issued, because an issued statement is sent.
 */
export function StatementDialog({
  view,
  open,
  onClose,
}: {
  view: CostRecoveryCaseView;
  open: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const issue = useIssueStatement(view.fileId);
  const [locale, setLocale] = useState<CostDocumentLocale>(lang.startsWith('fr') ? 'fr' : 'en');
  const [dueOn, setDueOn] = useState('');
  useEffect(() => {
    if (open) setDueOn(addDays(todayIso(), view.paymentTermsDays));
  }, [open, view.paymentTermsDays]);
  const valid = ISO.test(dueOn) && dueOn >= todayIso();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.costRecovery.statement.issueTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">
            {t('gov.costRecovery.statement.willBill', {
              amount: money(view.totals.unbilledCents, lang),
            })}
          </Typography>
          {!view.termsLocked && (
            <Alert severity="info">
              {view.terms.rateBps > 0
                ? t('gov.costRecovery.statement.termsFixed', {
                    rate: rate(view.terms.rateBps, lang),
                    grace: view.terms.graceDays,
                  })
                : t('gov.costRecovery.statement.noInterest')}
            </Alert>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              type="date"
              label={t('gov.costRecovery.statement.dueOn')}
              value={dueOn}
              onChange={(e) => setDueOn(e.target.value)}
              error={!valid}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: todayIso() } }}
            />
            <TextField
              select
              label={t('gov.costRecovery.documentLanguage')}
              value={locale}
              onChange={(e) => setLocale(e.target.value as CostDocumentLocale)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="en">{t('gov.costRecovery.language.en')}</MenuItem>
              <MenuItem value="fr">{t('gov.costRecovery.language.fr')}</MenuItem>
            </TextField>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!valid || issue.isPending || view.totals.unbilledCents <= 0}
          onClick={() => issue.mutate({ locale, dueOn }, { onSuccess: onClose })}
        >
          {t('gov.costRecovery.statement.issue')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Record money received. A cheque image or remittance can go with it. */
export function PaymentDialog({
  view,
  open,
  onClose,
}: {
  view: CostRecoveryCaseView;
  open: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const record = useRecordPayment(view.fileId);
  const fileInput = useRef<HTMLInputElement>(null);
  const [receivedOn, setReceivedOn] = useState(todayIso());
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('cheque');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [receipt, setReceipt] = useState<File | null>(null);
  useEffect(() => {
    if (!open) return;
    setReceivedOn(todayIso());
    setAmount('');
    setMethod('cheque');
    setReference('');
    setNote('');
    setReceipt(null);
  }, [open]);
  const cents = parseMoney(amount, lang);
  const valid = cents !== null && cents > 0 && ISO.test(receivedOn) && receivedOn <= todayIso();
  const over = cents !== null && cents > view.balance.balanceCents;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.costRecovery.payment.addTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('gov.costRecovery.payment.owing', {
              amount: money(view.balance.balanceCents, lang),
            })}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('gov.costRecovery.payment.amount')}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              error={amount !== '' && cents === null}
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            />
            <TextField
              type="date"
              label={t('gov.costRecovery.payment.receivedOn')}
              value={receivedOn}
              onChange={(e) => setReceivedOn(e.target.value)}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: todayIso() } }}
            />
          </Stack>
          {over && <Alert severity="warning">{t('gov.costRecovery.payment.over')}</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              label={t('gov.costRecovery.payment.method')}
              value={method}
              onChange={(e) => setMethod(e.target.value as PaymentMethod)}
              sx={{ minWidth: 200 }}
            >
              {PAYMENT_METHODS.map((m) => (
                <MenuItem key={m} value={m}>
                  {t(`gov.costRecovery.method.${m}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label={t('gov.costRecovery.payment.reference')}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 100 } }}
            />
          </Stack>
          <TextField
            label={t('gov.costRecovery.payment.note')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: 1000 } }}
          />
          <Box>
            <input
              ref={fileInput}
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/heic,image/tiff"
              hidden
              onChange={(e) => setReceipt(e.target.files?.[0] ?? null)}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<Icons.Paperclip size={iconSize.sm} />}
              onClick={() => fileInput.current?.click()}
            >
              {receipt ? receipt.name : t('gov.costRecovery.payment.attach')}
            </Button>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!valid || record.isPending}
          onClick={() =>
            cents !== null &&
            record.mutate(
              {
                dto: {
                  receivedOn,
                  amountCents: cents,
                  method,
                  reference: reference.trim() || undefined,
                  note: note.trim() || undefined,
                },
                receipt,
              },
              { onSuccess: onClose },
            )
          }
        >
          {t('gov.costRecovery.payment.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Agree a payment plan for today's balance, previewing the instalments. */
export function PlanDialog({
  view,
  open,
  onClose,
}: {
  view: CostRecoveryCaseView;
  open: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const create = useCreatePlan(view.fileId);
  const [count, setCount] = useState('6');
  const [firstDueOn, setFirstDueOn] = useState('');
  const [frequency, setFrequency] = useState<PlanFrequency>('monthly');
  useEffect(() => {
    if (!open) return;
    setCount('6');
    setFirstDueOn(addDays(todayIso(), 30));
    setFrequency('monthly');
  }, [open]);
  const n = Number(count);
  const valid =
    Number.isInteger(n) &&
    n >= 2 &&
    n <= COST_RECOVERY_LIMITS.maxPlanInstallments &&
    ISO.test(firstDueOn) &&
    firstDueOn >= todayIso();
  const preview = valid
    ? planSchedule({
        totalCents: view.balance.balanceCents,
        installmentCount: n,
        firstDueOn,
        frequency,
        agreedOn: todayIso(),
      })
    : [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.costRecovery.plan.addTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">
            {t('gov.costRecovery.plan.total', { amount: money(view.balance.balanceCents, lang) })}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              type="number"
              label={t('gov.costRecovery.plan.count')}
              value={count}
              onChange={(e) => setCount(e.target.value)}
              error={!Number.isInteger(n) || n < 2}
              slotProps={{ htmlInput: { min: 2, max: COST_RECOVERY_LIMITS.maxPlanInstallments } }}
            />
            <TextField
              select
              label={t('gov.costRecovery.plan.frequency')}
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as PlanFrequency)}
              sx={{ minWidth: 180 }}
            >
              {PLAN_FREQUENCIES.map((f) => (
                <MenuItem key={f} value={f}>
                  {t(`gov.costRecovery.plan.frequencies.${f}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type="date"
              label={t('gov.costRecovery.plan.firstDueOn')}
              value={firstDueOn}
              onChange={(e) => setFirstDueOn(e.target.value)}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: todayIso() } }}
            />
          </Stack>
          {preview.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('gov.costRecovery.plan.preview', {
                count: preview.length,
                amount: money(preview[0]?.amountCents ?? 0, lang),
                first: day(preview[0]?.dueOn ?? null, lang),
                last: day(preview.at(-1)?.dueOn ?? null, lang),
              })}
            </Typography>
          )}
          <Alert severity="info">{t('gov.costRecovery.plan.interestNote')}</Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!valid || create.isPending}
          onClick={() =>
            create.mutate({ installmentCount: n, firstDueOn, frequency }, { onSuccess: onClose })
          }
        >
          {t('gov.costRecovery.plan.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Escalate an overdue debt: to the tax roll (the reimbursement request to the
 * Department of Finance, French only) or as a lien (a certificate in either
 * language). Says plainly what will be generated before it is.
 */
export function EscalateDialog({
  view,
  open,
  onClose,
}: {
  view: CostRecoveryCaseView;
  open: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const escalate = useEscalate(view.fileId, view.localGovernmentId);
  const [kind, setKind] = useState<EscalationKind>('tax_roll');
  const [locale, setLocale] = useState<CostDocumentLocale>(lang.startsWith('fr') ? 'fr' : 'en');
  const [note, setNote] = useState('');
  useEffect(() => {
    if (open) setNote('');
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.costRecovery.escalate.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">
            {t('gov.costRecovery.escalate.summary', {
              amount: money(view.balance.balanceCents, lang),
              days: view.daysOverdue,
            })}
          </Typography>
          <TextField
            select
            label={t('gov.costRecovery.escalate.kind')}
            value={kind}
            onChange={(e) => setKind(e.target.value as EscalationKind)}
            helperText={t(`gov.costRecovery.escalate.${kind}Help`)}
          >
            {(['tax_roll', 'lien'] as const).map((k) => (
              <MenuItem key={k} value={k}>
                {t(`gov.costRecovery.escalation.${k}`)}
              </MenuItem>
            ))}
          </TextField>
          {kind === 'lien' && (
            <TextField
              select
              label={t('gov.costRecovery.documentLanguage')}
              value={locale}
              onChange={(e) => setLocale(e.target.value as CostDocumentLocale)}
              sx={{ maxWidth: 240 }}
            >
              <MenuItem value="en">{t('gov.costRecovery.language.en')}</MenuItem>
              <MenuItem value="fr">{t('gov.costRecovery.language.fr')}</MenuItem>
            </TextField>
          )}
          <TextField
            label={t('gov.costRecovery.escalate.note')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: 1000 } }}
          />
          <Alert severity="warning">{t('gov.costRecovery.escalate.warning')}</Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button
          variant="contained"
          color="error"
          disabled={escalate.isPending}
          onClick={() =>
            escalate.mutate(
              {
                kind,
                note: note.trim() || undefined,
                locale: kind === 'lien' ? locale : undefined,
              },
              { onSuccess: onClose },
            )
          }
        >
          {t(`gov.costRecovery.escalate.confirm.${kind}`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
